import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const problems = [
  'Website wirkt veraltet oder unprofessionell',
  'Kaum Anfragen trotz guter Leistung',
  'Niemand weiß, was Sie wirklich anbieten',
];

const solutions = [
  'Klarer Auftritt, der sofort Vertrauen schafft',
  'Mehr Kontaktanfragen direkt über die Website',
  'Ihre Stärken kommen endlich auf den Punkt',
];

const services = [
  {
    title: 'Design & Text',
    desc: 'Kein Baukasten, kein Copy-Paste – jeder Satz und jedes Layout entsteht neu für Sie.',
  },
  {
    title: 'Technik & Launch',
    desc: 'Schnell geladen, sicher gehostet, lokal gefunden – live in Wochen, nicht Monaten.',
  },
  {
    title: 'Persönliche Betreuung',
    desc: 'Sie erreichen mich direkt – keine Hotline, kein Ticket, keine Warteschleife.',
  },
];

export default function Benefits() {
  const { theme } = useTheme();
  const { ref, isVisible } = useScrollAnimation();
  const { ref: servRef, isVisible: servVisible } = useScrollAnimation();

  return (
    <section
      className="py-12 sm:py-20 md:py-28 px-4"
      style={{ backgroundColor: '#FFFFFF', fontFamily: theme.fonts.body }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-left mb-10 sm:mb-16">
          <span
            className="text-xs tracking-[0.3em] uppercase font-semibold mb-3 block"
            style={{ color: theme.colors.accent }}
          >
            Nicht nur sichtbar. Sondern wirksam.
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold"
            style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}
          >
            Ihre Website sollte für Sie arbeiten.<br />Nicht gegen Sie.
          </h2>
        </div>

        {/* Comparison */}
        <div
          ref={ref}
          className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          {/* Left – Problems */}
          <div className="p-6 sm:p-10 md:p-14" style={{ backgroundColor: '#F5F5F3' }}>
            <div className="flex items-center gap-2 mb-8">
              <div className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100">
                <i className="ri-close-line text-xs text-red-500"></i>
              </div>
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-gray-400">Häufige Situation</span>
            </div>
            <ul className="space-y-7">
              {problems.map((p, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-xl text-red-400"></i>
                  </div>
                  <span className="text-base font-medium leading-snug" style={{ color: theme.colors.text }}>
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right – Solutions */}
          <div className="p-6 sm:p-10 md:p-14" style={{ backgroundColor: '#0A0A0A' }}>
            <div className="flex items-center gap-2 mb-8">
              <div
                className="w-5 h-5 flex items-center justify-center rounded-full"
                style={{ background: `${theme.colors.accent}22` }}
              >
                <i className="ri-check-line text-xs" style={{ color: theme.colors.accent }}></i>
              </div>
              <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: theme.colors.accent }}>
                Mit Seitenschmied
              </span>
            </div>
            <ul className="space-y-7">
              {solutions.map((s, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i
                      className="ri-checkbox-circle-line text-xl"
                      style={{ color: theme.colors.accent }}
                    ></i>
                  </div>
                  <span className="text-base font-medium leading-snug text-white/85">
                    {s}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div
          className="my-10 sm:my-16 h-px"
          style={{ backgroundColor: `${theme.colors.text}10` }}
        />

        {/* Services */}
        <div className="mb-10">
          <h3
            className="text-2xl md:text-4xl font-bold mb-2 leading-tight"
            style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}
          >
            Text, Struktur, Wirkung.{' '}
            <span style={{
              background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Alles an der richtigen Stelle.
            </span>
          </h3>
        </div>

        <div
          ref={servRef}
          className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x"
          style={{
            opacity: servVisible ? 1 : 0,
            transform: servVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
          }}
        >
          {services.map((service, i) => (
            <div
              key={i}
              className="md:px-10 py-8 md:py-0 first:md:pl-0 last:md:pr-0"
            >
              <h4
                className="text-xl md:text-2xl font-bold mb-3 leading-tight"
                style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}
              >
                {service.title}
              </h4>
              <p
                className="text-base leading-relaxed"
                style={{ color: theme.colors.textSecondary }}
              >
                {service.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 sm:mt-14">
          <Link to="/ueber-uns">
            <button
              className="px-10 py-4 font-bold text-base rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                color: '#FFFFFF',
              }}
            >
              Mehr über Seitenschmied
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}
